import React, { useEffect, useState } from "react";
import { ShoppingBag, Package, Users, TrendingUp, Bell, Eye, UserCheck, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import api, { formatPrice } from "@/lib/api";

const cards = [
  { key: "total_orders", label: "Comenzi totale", icon: ShoppingBag, color: "bg-sky-100 text-sky-600" },
  { key: "new_orders", label: "Comenzi noi", icon: Bell, color: "bg-amber-100 text-amber-600" },
  { key: "total_products", label: "Produse", icon: Package, color: "bg-indigo-100 text-indigo-600" },
  { key: "total_users", label: "Clienți", icon: Users, color: "bg-emerald-100 text-emerald-600" },
];

const pageLabels = {
  "/": "Acasă",
  "/produse": "Produse",
  "/oferte": "Oferte",
  "/contact": "Contact",
  "/cos": "Coș",
  "/finalizare": "Finalizare",
  "/autentificare": "Autentificare",
  "/inregistrare": "Înregistrare",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [traffic, setTraffic] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((res) => setStats(res.data));
    api.get("/admin/traffic").then((res) => setTraffic(res.data));
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Panou de administrare</h1>
      <p className="mt-1 text-slate-500">Privire de ansamblu asupra magazinului Mimaso.</p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.key} className="rounded-2xl border border-slate-100 bg-white p-6" data-testid={`stat-${c.key}`}>
            <div className={`grid h-11 w-11 place-items-center rounded-xl ${c.color}`}>
              <c.icon className="h-5 w-5" />
            </div>
            <p className="mt-4 font-display text-3xl font-black text-slate-900">{stats ? stats[c.key] : "—"}</p>
            <p className="text-sm text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-100 bg-gradient-to-br from-sky-600 to-sky-500 p-8 text-white">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            <span className="label-caps">Venit total (estimativ)</span>
          </div>
          <p className="mt-3 font-display text-4xl font-black">{stats ? formatPrice(stats.revenue) : "—"}</p>
          <p className="mt-1 text-sky-100">Din toate comenzile neanulate</p>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid="traffic-views">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-violet-100 text-violet-600"><Eye className="h-5 w-5" /></div>
          <p className="mt-4 font-display text-3xl font-black text-slate-900">{traffic ? traffic.today_views : "—"}</p>
          <p className="text-sm text-slate-500">Vizualizări azi · {traffic ? traffic.total_views : "—"} total</p>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid="traffic-visitors">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-rose-100 text-rose-600"><UserCheck className="h-5 w-5" /></div>
          <p className="mt-4 font-display text-3xl font-black text-slate-900">{traffic ? traffic.unique_today : "—"}</p>
          <p className="text-sm text-slate-500">Vizitatori unici azi · {traffic ? traffic.unique_total : "—"} total</p>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-100 bg-white p-6 lg:col-span-2" data-testid="traffic-chart">
          <div className="mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-sky-600" />
            <h3 className="font-display text-lg font-bold text-slate-900">Trafic ultimele 7 zile</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={traffic?.series || []}>
                <XAxis dataKey="date" tickLine={false} axisLine={false} fontSize={12} stroke="#94a3b8" />
                <Tooltip cursor={{ fill: "#f1f5f9" }} formatter={(v) => [`${v} vizualizări`, ""]} labelFormatter={(l) => `Ziua ${l}`} />
                <Bar dataKey="views" fill="#0284C7" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid="traffic-top-pages">
          <h3 className="mb-4 font-display text-lg font-bold text-slate-900">Cele mai vizitate pagini</h3>
          {traffic?.top_pages?.length ? (
            <ul className="space-y-3">
              {traffic.top_pages.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm">
                  <span className="truncate text-slate-700">{pageLabels[p.path] || p.path}</span>
                  <span className="ml-2 shrink-0 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-bold text-sky-700">{p.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">Încă nu există date de trafic.</p>
          )}
        </div>
      </div>
    </div>
  );
}
